'use client';

// This hook is used to prefetch links in a Next.js application, more advanced than using prefetch={true} on Link

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** ------------ module-level state (per tab) ------------ */
// Dedup already-prefetched routes
const PREFETCHED = new Set<string>();
// Dedup concurrent prefetches (avoid double hits)
const INFLIGHT = new Map<string, Promise<void>>();
// Simple concurrency control
let ACTIVE = 0;
const QUEUE: Array<() => Promise<void>> = [];

const runQueue = async (maxConcurrent: number) => {
  while (ACTIVE < maxConcurrent && QUEUE.length) {
    const job = QUEUE.shift()!;
    ACTIVE++;
    try {
      await job();
    } catch {
      /* swallow — navigation will recover on click anyway */
    } finally {
      ACTIVE--;
      runQueue(maxConcurrent);
    }
  }
};

const enqueue = (job: () => Promise<void>, maxConcurrent: number) => {
  QUEUE.push(job);
  runQueue(maxConcurrent);
};

/** ------------ helpers ------------ */
const hasRIC = typeof window !== 'undefined' && 'requestIdleCallback' in window;
const requestIdle = hasRIC
  ? (cb: () => void) => (window as any).requestIdleCallback(cb, { timeout: 2000 })
  : (cb: () => void) => {
      // Use rAF to avoid blocking initial paint, then setTimeout for gentle delay
      requestAnimationFrame(() => setTimeout(cb, 500));
    };
const cancelIdle = hasRIC
  ? (id: number) => (window as any).cancelIdleCallback(id)
  : (id: number) => window.clearTimeout(id as number);

const isInternalHref = (href: string) => {
  // next/link internal routes are typically relative: "/products/123"
  // allow same-origin absolute urls too
  if (!href) return false;
  if (href.startsWith('#')) return false; // skip hash-only links
  if (href.startsWith('/')) return true;
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
};

/** ------------ types ------------ */
export type UseLinkPrefetchOptions = {
  href: string;

  /** High-priority prefetch on hover/focus */
  prefetchOnHover?: boolean;

  /** Low-priority prefetch when link becomes visible */
  prefetchWhenVisible?: boolean;

  /** IO root margin for early visibility */
  rootMargin?: string; // default '400px'

  /** Delay (ms) before triggering hover prefetch (filters drive-bys) */
  hoverDelayMs?: number; // default 80

  /** Max concurrent prefetch requests across the tab */
  maxConcurrent?: number; // default 4
};

type BindProps = {
  ref: (el: HTMLElement | null) => void;
  onPointerEnter?: (e: any) => void;
  onPointerLeave?: (e: any) => void;
  onFocus?: (e: any) => void;
};

/** ------------ hook ------------ */
export const useLinkPrefetch = ({
  href,
  prefetchOnHover = true,
  prefetchWhenVisible = true,
  rootMargin = '400px',
  hoverDelayMs = 80,
  maxConcurrent = 4,
}: UseLinkPrefetchOptions): Readonly<BindProps> => {
  const router = useRouter();

  // local flags (per element instance)
  const [didHigh, setDidHigh] = useState(false);
  const [didLow, setDidLow] = useState(false);

  const elRef = useRef<HTMLElement | null>(null);
  const ioRef = useRef<IntersectionObserver | null>(null);
  const idleIdRef = useRef<number | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  const conn = (typeof navigator !== 'undefined' && (navigator as any).connection) || null;
  const saveData = !!conn?.saveData;
  const isVerySlow = !!(conn && (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g'));

  const isTouchOnly = useMemo(() => {
    if (typeof window === 'undefined') return false;
    // treat no mouse + touch as touch-only; still allow keyboard focus to prefetch
    const hasTouch =
      'ontouchstart' in window ||
      (navigator as any).maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0;
    // We still use pointer events; "touch-only" only disables hover-triggered prefetch.
    return !!hasTouch;
  }, []);

  const doPrefetch = useCallback(async () => {
    if (!isInternalHref(href)) return; // skip external anchors
    if (PREFETCHED.has(href)) return;

    // In-flight dedupe
    const existing = INFLIGHT.get(href);
    if (existing) {
      await existing;
      return;
    }

    const prefetchPromise = (async () => {
      try {
        await router.prefetch(href);
        PREFETCHED.add(href);
        INFLIGHT.delete(href);
      } catch (e) {
        // ignore — on actual click, Next will fetch anyway
        INFLIGHT.delete(href);
        throw e;
      }
    })();

    INFLIGHT.set(href, prefetchPromise);
    await prefetchPromise;
  }, [href, router]);

  // High priority (intent via hover/focus)
  const triggerHigh = useCallback(() => {
    if (didHigh || saveData || isVerySlow) return;
    enqueue(doPrefetch, maxConcurrent);
    setDidHigh(true);
    setDidLow(true); // high covers low
  }, [didHigh, saveData, isVerySlow, doPrefetch, maxConcurrent]);

  // Low priority (visible → idle time)
  const triggerLow = useCallback(() => {
    if (didLow || saveData || isVerySlow || PREFETCHED.has(href)) return;
    // Skip if page is not visible (background tab)
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    idleIdRef.current = requestIdle(() => {
      enqueue(doPrefetch, maxConcurrent);
      setDidLow(true);
      idleIdRef.current = null;
    }) as unknown as number;
  }, [didLow, saveData, isVerySlow, href, doPrefetch, maxConcurrent]);

  // Setup visibility observer when ref is set
  const setupVisibilityObserver = useCallback(
    (el: HTMLElement) => {
      if (!prefetchWhenVisible || saveData || isVerySlow) return;
      if (typeof IntersectionObserver === 'undefined') return;

      // Clean up any existing observer
      if (ioRef.current) {
        ioRef.current.disconnect();
        ioRef.current = null;
      }

      // Check if already visible before setting up observer
      const checkInitialVisibility = () => {
        const rect = el.getBoundingClientRect();
        const rootMarginPx = parseInt(rootMargin, 10) || 400;
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;

        const isVisible =
          rect.top < windowHeight + rootMarginPx &&
          rect.bottom > -rootMarginPx &&
          rect.left < windowWidth + rootMarginPx &&
          rect.right > -rootMarginPx;

        if (isVisible) {
          triggerLow();
          return true;
        }
        return false;
      };

      // If already visible, don't set up observer
      if (checkInitialVisibility()) {
        return;
      }

      // Set up observer for future visibility
      ioRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting) {
            triggerLow();
            ioRef.current?.disconnect();
          }
        },
        { rootMargin },
      );

      ioRef.current.observe(el);
    },
    [prefetchWhenVisible, saveData, isVerySlow, rootMargin, triggerLow],
  );

  // Bindings
  const bind: BindProps = {
    ref: (el) => {
      elRef.current = el;
      if (el) {
        setupVisibilityObserver(el);
      }
    },
  };

  // Pointer hover with small delay (filters drive-by)
  if (prefetchOnHover && !isTouchOnly) {
    bind.onPointerEnter = () => {
      if (hoverTimerRef.current != null) return;
      hoverTimerRef.current = window.setTimeout(() => {
        triggerHigh();
        hoverTimerRef.current = null;
      }, hoverDelayMs);
    };
    bind.onPointerLeave = () => {
      if (hoverTimerRef.current != null) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }

  // Keyboard focus (always allowed — good for a11y)
  bind.onFocus = prefetchOnHover ? () => triggerHigh() : undefined;

  // Cleanup timers on unmount or href change
  useEffect(() => {
    return () => {
      if (idleIdRef.current != null) {
        cancelIdle(idleIdRef.current);
        idleIdRef.current = null;
      }
      if (hoverTimerRef.current != null) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, []);

  // If a component is reused with a new href, reset local flags
  useEffect(() => {
    setDidHigh(false);
    setDidLow(false);
  }, [href]);

  return bind;
};
